import { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BulkBar, SelectAllBox, SelectBox, useSelection } from "@/components/shared/BulkSelect";
import { adapter } from "@/services/db";
import { useAuth } from "@/context/AuthContext";
import { DEFAULT_PRODUCT_CATEGORIES, slugOf, sortCats, productCategoryOf } from "@/lib/productCategories";
import { cn } from "@/lib/utils";
import { CategoryRulesDialog, categoryRulesSummary } from "./ShopRules";

const errMsg = (e) => (e?.code === "permission-denied" ? "Permission denied — publish the product_categories rule (see notes)" : e?.message);
const now = () => new Date().toISOString();
const log = (user, id, action, description) => adapter.add("activity_logs", { app: "ops", entity_type: "product_categories", entity_id: id, action, change_type: action, description, performed_by: user?.email || null, performed_by_role: user?.role || null, status: "applied", created_at: now(), server_ts: now() }).catch(() => {});

function AddDialog({ open, onClose, existing, nextOrder }) {
  const { user } = useAuth();
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const names = [...new Set(txt.split(/[\n,]/).map((x) => x.trim()).filter(Boolean))];
  const have = new Set(existing.map((c) => c.name.toLowerCase()));
  const fresh = names.filter((n) => !have.has(n.toLowerCase()));
  const add = async () => {
    setBusy(true);
    try {
      let order = nextOrder;
      for (const name of fresh) {
        let id = slugOf(name);
        while (existing.some((c) => c.id === id)) id = `${id}-2`;
        await adapter.set("product_categories", id, { name, slug: id, order: order++, hidden: false, aliases: [], created_at: now(), updated_at: now(), created_by: user?.email || null });
      }
      await log(user, "bulk", "product_categories_added", fresh.join(", "));
      toast.success(`${fresh.length} product categor${fresh.length === 1 ? "y" : "ies"} added`); setTxt(""); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="pcat-add">
        <DialogHeader><DialogTitle className="font-heading">Add product categories</DialogTitle>
          <DialogDescription>One per line (or comma separated). They appear as shop headings on brugo.in, in this order, after the existing ones.</DialogDescription></DialogHeader>
        <Textarea rows={8} value={txt} onChange={(e) => setTxt(e.target.value)} placeholder={"Tiles\nTaps & Faucets\nFevicol & Adhesives\nBathroom Fittings\nLights\nDiwali Lights"} data-testid="pcat-add-text" />
        <p className="text-xs text-muted-foreground">{fresh.length} new{names.length - fresh.length ? ` · ${names.length - fresh.length} already exist (skipped)` : ""}</p>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={add} disabled={busy || !fresh.length} data-testid="pcat-add-save">{busy ? "Adding…" : `Add ${fresh.length}`}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ cat, onClose, products, cats }) {
  const { user } = useAuth();
  const [name, setName] = useState(cat?.name || "");
  const [aliases, setAliases] = useState((cat?.aliases || []).join(", "));
  const [busy, setBusy] = useState(false);
  if (!cat) return null;
  const save = async () => {
    const nm = name.trim();
    if (!nm) return toast.error("Name can't be empty");
    if (cats.some((c) => c.id !== cat.id && c.name.toLowerCase() === nm.toLowerCase())) return toast.error("Another category already has that name");
    setBusy(true);
    try {
      await adapter.update("product_categories", cat.id, { name: nm, aliases: aliases.split(",").map((x) => x.trim()).filter(Boolean), updated_at: now() });
      // Renamed: move its products to the new name so they stay under this heading.
      if (nm !== cat.name) {
        const ids = products.filter((p) => p.shop_category === cat.name).map((p) => p.id);
        if (ids.length) await adapter.batchPatchFields("products", ids, { shop_category: nm, updated_at: now() });
        await log(user, cat.id, "product_category_renamed", `${cat.name} → ${nm} (${ids.length} products moved)`);
      }
      toast.success("Saved"); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="pcat-edit">
        <DialogHeader><DialogTitle className="font-heading">Edit {cat.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><p className="text-xs text-muted-foreground">Name on the shop</p><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="pcat-edit-name" /></div>
          <div className="space-y-1"><p className="text-xs text-muted-foreground">Older names that mean the same (comma separated)</p><Input value={aliases} onChange={(e) => setAliases(e.target.value)} placeholder="e.g. Paints, Painting" />
            <p className="text-[11px] text-muted-foreground">Products saved with one of these as their category show under this heading until you assign them properly.</p></div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ProductCategoriesTab({ cats, products, onShowUncategorised }) {
  const { user } = useAuth();
  const [adding, setAdding] = useState(false);
  const [edit, setEdit] = useState(null);
  const [rulesFor, setRulesFor] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const sorted = useMemo(() => [...cats].sort(sortCats), [cats]);
  const live = products.filter((p) => p.status !== "inactive");
  const countOf = useMemo(() => {
    const m = {};
    live.forEach((p) => { const k = productCategoryOf(p, cats); m[k || "__none"] = (m[k || "__none"] || 0) + 1; });
    return m;
  }, [live, cats]);
  const s = useSelection(sorted);

  const seed = async () => {
    setSeeding(true);
    try {
      let i = 0;
      for (const [name, aliases] of DEFAULT_PRODUCT_CATEGORIES) {
        const id = slugOf(name);
        if (!cats.some((c) => c.id === id)) await adapter.set("product_categories", id, { name, slug: id, order: i, hidden: false, aliases, created_at: now(), updated_at: now(), created_by: user?.email || null });
        i++;
      }
      await log(user, "seed", "product_categories_seeded", "brugo.in default headings");
      toast.success("Added brugo.in's current shop headings");
    } catch (e) { toast.error(errMsg(e)); } finally { setSeeding(false); }
  };
  const setHidden = async (c, hidden) => {
    try { await adapter.update("product_categories", c.id, { hidden, updated_at: now() }); await log(user, c.id, hidden ? "product_category_hidden" : "product_category_shown", c.name); toast.success(`${c.name} ${hidden ? "hidden from" : "shown on"} brugo.in`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (c, dir) => {
    const i = sorted.findIndex((x) => x.id === c.id), j = i + dir;
    if (j < 0 || j >= sorted.length) return;
    const list = [...sorted]; [list[i], list[j]] = [list[j], list[i]];
    try { await Promise.all(list.map((x, k) => (x.order === k ? null : adapter.update("product_categories", x.id, { order: k, updated_at: now() })))); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const actions = [
    { key: "hide", label: "Hide on brugo.in", icon: "EyeOff", eligible: (c) => !c.hidden, skipNote: "already hidden",
      description: "The heading and its products disappear from the shop. Nothing is deleted.",
      run: async (c) => { await adapter.update("product_categories", c.id, { hidden: true, updated_at: now() }); await log(user, c.id, "product_category_hidden", c.name); } },
    { key: "show", label: "Show on brugo.in", icon: "Eye", eligible: (c) => !!c.hidden, skipNote: "already showing",
      run: async (c) => { await adapter.update("product_categories", c.id, { hidden: false, updated_at: now() }); await log(user, c.id, "product_category_shown", c.name); } },
    { key: "merge", label: "Merge into…", icon: "Merge",
      description: "Moves every product of the selected categories into one category, remembers the old names, and removes the merged categories.",
      ask: [{ key: "target", label: "Merge into", type: "select", required: true, options: sorted.map((c) => [c.id, c.name]) }],
      once: async (docs, v) => {
        const target = cats.find((c) => c.id === v.target);
        const from = docs.filter((c) => c.id !== target.id);
        const names = from.map((c) => c.name);
        const ids = live.filter((p) => names.includes(productCategoryOf(p, cats))).map((p) => p.id);
        if (ids.length) await adapter.batchPatchFields("products", ids, { shop_category: target.name, updated_at: now() });
        await adapter.update("product_categories", target.id, { aliases: [...new Set([...(target.aliases || []), ...names, ...from.flatMap((c) => c.aliases || [])])], updated_at: now() });
        for (const c of from) await adapter.remove("product_categories", c.id);
        await log(user, target.id, "product_categories_merged", `${names.join(", ")} → ${target.name} (${ids.length} products)`);
      } },
    { key: "delete", label: "Delete", icon: "Trash2", danger: true, eligible: (c) => !countOf[c.name], skipNote: "still have products — merge them or move the products first",
      run: async (c) => { await adapter.remove("product_categories", c.id); await log(user, c.id, "product_category_deleted", c.name); } },
  ];

  return (
    <Card data-testid="pcat-tab">
      <div className="p-4 border-b border-border flex flex-wrap items-center gap-2">
        <p className="text-xs text-muted-foreground max-w-xl">Shop headings on brugo.in (Tiles, Taps, Lights…) — separate from services. Each product picks one in its editor. Hidden ones disappear from the shop with their products.</p>
        <div className="ml-auto flex gap-2">
          {!cats.length && <Button size="sm" variant="outline" onClick={seed} disabled={seeding} data-testid="pcat-seed"><Icons.Download className="h-4 w-4 mr-1" />{seeding ? "Adding…" : "Start with brugo.in's current 20"}</Button>}
          <Button size="sm" onClick={() => setAdding(true)} data-testid="pcat-add-open"><Icons.Plus className="h-4 w-4 mr-1" />Add categories</Button>
        </div>
      </div>
      {countOf.__none > 0 && (
        <button type="button" onClick={onShowUncategorised} className="w-full text-left px-4 py-2 text-xs bg-amber-50/70 text-amber-800 border-b border-amber-100 hover:bg-amber-50" data-testid="pcat-uncategorised">
          <Icons.AlertTriangle className="inline h-3.5 w-3.5 mr-1" />{countOf.__none} product{countOf.__none === 1 ? " has" : "s have"} no product category and won't show under any heading — select them in Products and use “Set product category”.
        </button>
      )}
      <div className="px-3 pt-3"><BulkBar s={s} path="product_categories" rows={sorted} label="categories" actions={actions} nameOf={(c) => c.name} /></div>
      <Table>
        <TableHeader><TableRow><TableHead className="w-8"><SelectAllBox s={s} testId="pcat-select-all" /></TableHead><TableHead className="w-16">Order</TableHead><TableHead>Category</TableHead><TableHead>Products</TableHead><TableHead>Older names</TableHead><TableHead>Order rules</TableHead><TableHead>On brugo.in</TableHead><TableHead className="text-right">Edit</TableHead></TableRow></TableHeader>
        <TableBody>
          {sorted.map((c, i) => (
            <TableRow key={c.id} className={cn(c.hidden && "opacity-60", s.sel.has(c.id) && "bg-primary/5")} data-testid={`pcat-row-${c.id}`}>
              <TableCell><SelectBox s={s} id={c.id} /></TableCell>
              <TableCell className="whitespace-nowrap">
                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(c, -1)} title="Move up"><Icons.ChevronUp className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === sorted.length - 1} onClick={() => move(c, 1)} title="Move down"><Icons.ChevronDown className="h-4 w-4" /></Button>
              </TableCell>
              <TableCell className="font-medium">{c.name}{c.hidden && <span className="ml-2 text-[10px] rounded-full bg-secondary px-1.5 py-0.5 text-muted-foreground">hidden</span>}</TableCell>
              <TableCell className="font-mono text-sm">{countOf[c.name] || 0}</TableCell>
              <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate">{(c.aliases || []).join(", ") || "—"}</TableCell>
              <TableCell className="text-xs max-w-[260px]">
                <button type="button" onClick={() => setRulesFor(c)} className="text-left hover:underline" data-testid={`pcat-rules-${c.id}`}>
                  <Icons.SlidersHorizontal className="inline h-3.5 w-3.5 mr-1 text-primary" />{categoryRulesSummary(c) || <span className="text-muted-foreground">Set rules</span>}
                </button>
              </TableCell>
              <TableCell><Switch checked={!c.hidden} onCheckedChange={(v) => setHidden(c, !v)} data-testid={`pcat-visible-${c.id}`} /></TableCell>
              <TableCell className="text-right"><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEdit(c)} data-testid={`pcat-edit-${c.id}`}><Icons.Pencil className="h-4 w-4" /></Button></TableCell>
            </TableRow>
          ))}
          {!cats.length && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No product categories yet. Start with brugo.in's current 20 headings, then add yours (Tiles, Taps, Diwali Lights…).</TableCell></TableRow>}
        </TableBody>
      </Table>
      <AddDialog open={adding} onClose={() => setAdding(false)} existing={cats} nextOrder={sorted.length} />
      {edit && <EditDialog key={edit.id} cat={edit} onClose={() => setEdit(null)} products={products} cats={cats} />}
      {rulesFor && <CategoryRulesDialog key={rulesFor.id} cat={rulesFor} onClose={() => setRulesFor(null)} />}
    </Card>
  );
}
